import {captureMessage} from '@sentry/nextjs';

import {
  type FulfillmentDetailUpdateOptions,
  useGlobalFulfillmentDetail,
  useGlobalFulfillmentDetailMutation,
} from '@/components/global/providers/GlobalFulfillmentDetailProvider';
import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import {
  type FulfillmentDetailAddressInput,
  type FulfillmentDetailUpdateInput,
} from '@/graphql/types';
import {useGoogleMapsGeocode} from '@/hooks/useGoogleMapsGeocode';
import {fulfillmentDetailAddressInputToString} from '@/utils/geocodedAddressResult';
import {geocodeStringToFulfillmentAddress} from '@/utils/geocodeStringToFulfillmentAddress';
import {useEventBarV2, type WhereSelection} from '../EventBarV2Provider';
import {
  buildFulfillmentCreateInput,
  buildFulfillmentUpdateFromState,
  EMPTY_FULFILLMENT_BASELINE,
  type FulfillmentUpdateFromStateArgs,
} from '../utils/fulfillment';
import {useEventBarFallbackAddress} from './useEventBarFallbackAddress';

// An explicit set of field values to commit, independent of the provider's staged field state —
// used when re-running a past search, where the values come from the clicked entry, not the bar.
export type EventBarCommitValues = Omit<
  FulfillmentUpdateFromStateArgs,
  'hasUserEditedWhere' | 'fulfillmentDetail'
>;

// The outcome of a commit. `ok` is false only when the mutation threw (network/validation/entity),
// and true otherwise — including the no-op case where nothing changed. `createdFulfillmentDetailId`
// is set only when a new detail was *created* (a page with no active detail), so a no-detail search
// can point itself at it; an in-place update keeps the same id and leaves it undefined.
export type CommitFulfillmentResult = {ok: boolean; createdFulfillmentDetailId?: string};

// Flushes the staged EventBarV2 field state to the fulfillment detail as a single commit at search time.
export const useCommitEventBarFulfillment = () => {
  const {updateFulfillmentDetail} = useGlobalFulfillmentDetailMutation();
  const {fulfillmentDetail} = useGlobalFulfillmentDetail();
  const {isOnMenuPage} = useStoreNextRouter();
  const fallbackAddress = useEventBarFallbackAddress();
  const {fetchGeocodedAddress} = useGoogleMapsGeocode();
  const {
    whereSelection,
    setWhereSelection,
    markFieldEngaged,
    hasUserEditedWhereRef,
    createdFulfillmentDetailIdRef,
    fulfillmentType,
    whenDate,
    whenTime,
    headCount,
    eventName,
  } = useEventBarV2();

  // Geocodes an address that has no coordinates, street and ZIP included (a caterer's city is just
  // "City, ST"). Geocode results have no suite, so the address's own is kept. A failed lookup keeps
  // the address as-is rather than failing the search.
  const withCoordinates = async (
    address: FulfillmentDetailAddressInput,
  ): Promise<FulfillmentDetailAddressInput | undefined> => {
    if (address.latitude != null && address.longitude != null) return address;
    try {
      const geocoded = await geocodeStringToFulfillmentAddress(
        fulfillmentDetailAddressInputToString(address),
        {
          fetchGeocodedAddress,
          fallbackAddress: address,
        },
      );
      return geocoded && {...geocoded, street2: geocoded.street2 ?? address.street2 ?? null};
    } catch {
      return address;
    }
  };

  // Persists the built input and reports the outcome (see CommitFulfillmentResult). A null input is
  // a no-op and reports success. On a caught failure the edit-tracking is left intact so the pending
  // edits aren't silently reconciled away.
  const runCommit = async (
    input: FulfillmentDetailUpdateInput | null,
    options: FulfillmentDetailUpdateOptions,
  ): Promise<CommitFulfillmentResult> => {
    if (!input) return {ok: true};
    try {
      const {fulfillmentDetail: committed} = await updateFulfillmentDetail(input, options);
      // A successful commit makes the staged state the new persisted state, so clear the Where
      // edit-tracking.
      hasUserEditedWhereRef.current = false;
      // An address in the input means the user supplied one, which settles Where. Record it before
      // the selection is dropped below, since that is all the ask flow has left to go on afterwards.
      // Opening the Where panel settles it too, so this matters for the paths that supply an address
      // without ever showing that panel — replaying a past search from the What panel, above all.
      if (input.address || input.addressId) markFieldEngaged('where');
      setWhereSelection(null);
      // Only a *created* detail (none was active) hands back an id; an in-place update keeps its own.
      const createdFulfillmentDetailId = fulfillmentDetail ? undefined : committed?.id;
      if (createdFulfillmentDetailId)
        createdFulfillmentDetailIdRef.current = createdFulfillmentDetailId;
      return {ok: true, createdFulfillmentDetailId};
    } catch (error) {
      // updateFulfillmentDetail throws on network failures and on validation/entity errors. Entity
      // locked/not-found already redirect with a flash message; other failures aren't surfaced in the
      // bar. Log and report {ok: false} (rather than throw) so the search flow stays best-effort while
      // the menu-page edit surfaces keep themselves open instead of implying a save that didn't happen.
      captureMessage('EventBarV2 search fulfillment detail commit failed', {
        level: 'warning',
        extra: {input, error},
      });
      return {ok: false};
    }
  };

  // `whereOverride` is the selection the search guard just resolved from a typed-but-unselected
  // address; its `setWhereSelection` state update isn't visible yet, so take it directly to avoid
  // committing the search against the stale (pre-resolution) address.
  //
  // The search flow reads `createdFulfillmentDetailId` and proceeds best-effort; the menu-page edit
  // surfaces only close themselves on `ok`, so a failed save never closes a panel or the catch-all
  // modal while implying it succeeded.
  const commitFulfillment = async (
    whereOverride?: WhereSelection,
    {skipUrlSync = false}: FulfillmentDetailUpdateOptions = {},
  ): Promise<CommitFulfillmentResult> => {
    const values = {
      whereSelection: whereOverride ?? whereSelection,
      hasUserEditedWhere: whereOverride ? true : hasUserEditedWhereRef.current,
      fulfillmentType,
      whenDate,
      whenTime,
      headCount,
      eventName,
    };
    if (!fulfillmentDetail && isOnMenuPage) {
      const input = buildFulfillmentUpdateFromState({
        ...values,
        fulfillmentDetail: EMPTY_FULFILLMENT_BASELINE,
      });
      // Without a Where pick, the menu create would fill in the caterer's city with no coordinates.
      // Send the address the bar shows instead, geocoding any address that lacks them.
      const address = input?.address ?? fallbackAddress;
      if (input && !input.addressId && address) input.address = await withCoordinates(address);
      return runCommit(input, {skipUrlSync});
    }
    // No active detail on this page (home, start-new-order, …): create one from the staged fields so
    // the search carries the chosen address/date/headcount instead of a locationless search.
    if (!fulfillmentDetail) return runCommit(buildFulfillmentCreateInput(values), {skipUrlSync});
    return runCommit(buildFulfillmentUpdateFromState({...values, fulfillmentDetail}), {
      skipUrlSync,
    });
  };

  // Commit an explicit set of field values (e.g. re-running a past search) rather than the provider's
  // staged state — the caller's `setWhereSelection`/`setWhen…` updates aren't visible yet this tick,
  // so committing from state would persist the stale (pre-restore) values. No create path here:
  // history clicks reuse the entry's own detail on no-detail pages (see useApplySearchHistoryEntry).
  const commitFulfillmentValues = async (values: EventBarCommitValues): Promise<void> => {
    if (!fulfillmentDetail) return;
    await runCommit(
      buildFulfillmentUpdateFromState({...values, hasUserEditedWhere: true, fulfillmentDetail}),
      {},
    );
  };

  return {commitFulfillment, commitFulfillmentValues};
};
